import React, { FC, ReactNode, useCallback } from "react";

import { StepActionButton } from "./StepActionButton";
import {
  StyledStepTitle,
  StyledStepHeaderLeft,
  StyledStepHeader,
  StyledCustomStep,
} from "./Styles";
import { Dialog } from "../Dialog";
import { IconButton } from "../IconButton";
import { ArrowBackMobileIcon, CancelCrossIcon } from "../Icons";
import { Caption2, Header3 } from "../Typography";

export interface IStep {
  index?: number;
  isLast?: boolean;
  subtitle: string;
  title?: string;
  onNextButtonClick?: () => void;
  onPreviousButtonClick?: () => void;
  onClose?: () => void;
  children: ReactNode;
  actionLabel?: string;
  shouldRenderNextButton?: boolean;
  changingStepInProgress?: boolean;
  disableBackButton?: boolean;
  customRightHeader?: ReactNode;
  skip?: boolean;
}

export const Step: FC<IStep> = ({
  index,
  isLast,
  subtitle,
  title,
  onNextButtonClick,
  onPreviousButtonClick,
  onClose,
  children,
  actionLabel = "Proceed",
  shouldRenderNextButton = true,
  changingStepInProgress = false,
  disableBackButton = false,
  customRightHeader,
  skip = false,
}) => {
  const onActionButtonClick = useCallback(() => {
    if (isLast && onClose) {
      return onClose();
    }

    return onNextButtonClick && onNextButtonClick();
  }, [isLast, onClose, onNextButtonClick]);

  return !skip ? (
    <>
      <StyledStepHeader>
        <StyledStepHeaderLeft>
          {!disableBackButton && index && index > 0 ? (
            <IconButton
              onClick={onPreviousButtonClick}
              variant="text gray"
              className="tw--ml-3 tw-mr-2"
              disabled={changingStepInProgress}
            >
              <ArrowBackMobileIcon />
            </IconButton>
          ) : null}
          <StyledStepTitle>
            <Caption2>{title}</Caption2>
            <Header3>{subtitle}</Header3>
          </StyledStepTitle>
        </StyledStepHeaderLeft>
        {customRightHeader ? (
          customRightHeader
        ) : (
          <IconButton
            onClick={onClose}
            variant="text gray"
            disabled={changingStepInProgress}
          >
            <CancelCrossIcon />
          </IconButton>
        )}
      </StyledStepHeader>
      <Dialog.ContentDivider />
      {shouldRenderNextButton ? (
        <>
          <Dialog.Content>{children}</Dialog.Content>
          <Dialog.ContentDivider />
          <Dialog.Actions>
            <StepActionButton
              isLast={isLast}
              actionLabel={actionLabel}
              onNextButtonClick={onActionButtonClick}
            />
          </Dialog.Actions>
        </>
      ) : (
        <StyledCustomStep>{children}</StyledCustomStep>
      )}
    </>
  ) : null;
};

Step.displayName = "Step";
